import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Home, Users, Calendar, MessageSquare, DollarSign, Heart, CreditCard,
  Menu, LogOut, X, HandHeart, Shield, ChevronDown,
  Bell, User, BookUser, UserPlus, Award, GraduationCap, HelpCircle,
  Globe, ShieldAlert
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import axios from 'axios';
import NotificationSettings from './NotificationSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  const isAdmin = user?.role === 'super_admin' || user?.role === 'league_admin' || user?.role === 'treasurer';
  const isFinanceRole = user?.role === 'super_admin' || user?.role === 'treasurer';
  // Staff capability is now derived from team membership (server-computed),
  // not from the global users.role string. A parent who is on a team's
  // `team_moms` array is still a staff user for that team.
  const teamAssignmentCount = (user?.team_assignments || []).length;
  const isCoach = !!user?.has_any_staff_assignment;
  const isDualRole = isAdmin || isCoach;

  // Role switcher state — persisted in sessionStorage
  const [activeView, setActiveView] = useState(() => {
    return sessionStorage.getItem('pcc_active_view') || 'parent';
  });

  useEffect(() => {
    sessionStorage.setItem('pcc_active_view', activeView);
  }, [activeView]);

  // Staff label: prefer team-membership signal so a parent-globally + team-mom
  // user shows the right label. When the user has team assignments on more
  // than one team, append a "(N teams)" qualifier.
  const teamRoleLabels = (user?.team_assignments || []).map((a) => a.team_role);
  const hasCoachRole = teamRoleLabels.some((r) => r === 'head_coach' || r === 'assistant_coach');
  const hasTeamMomRole = teamRoleLabels.includes('team_mom');
  let staffLabel;
  if (isAdmin) {
    staffLabel = 'Admin';
  } else if (hasCoachRole && hasTeamMomRole) {
    staffLabel = 'Staff';
  } else if (hasCoachRole) {
    staffLabel = 'Coach';
  } else if (hasTeamMomRole) {
    staffLabel = 'Team Mom';
  } else {
    // Fallback (covers legacy global-only roles)
    staffLabel = user?.role === 'team_mom' ? 'Team Mom' : 'Coach';
  }
  if (!isAdmin && teamAssignmentCount > 1) {
    staffLabel = `${staffLabel} (${teamAssignmentCount} teams)`;
  }

  const handleViewSwitch = (view) => {
    setActiveView(view);
    setMobileMenuOpen(false);
    if (view === 'parent') navigate('/dashboard');
    else if (isAdmin) navigate('/admin/dashboard');
    else navigate('/coach/dashboard');
  };

  // Fetch unread message count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dm/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Refresh count when navigating to/from DM page
  useEffect(() => {
    if (location.pathname === '/dm') {
      // Slight delay to allow messages to be marked as read
      const timeout = setTimeout(fetchUnreadCount, 1000);
      return () => clearTimeout(timeout);
    }
  }, [location.pathname, fetchUnreadCount]);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Track scroll for header shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigation = [
    { name: 'Home', path: '/dashboard', icon: Home },
    { name: 'My Family', path: '/family', icon: Users },
    { name: 'Schedule', path: '/schedule', icon: Calendar },
    { name: 'Communications', path: '/communications', icon: MessageSquare },
    { name: 'Fundraising', path: '/fundraising', icon: DollarSign },
    { name: 'Volunteer', path: '/volunteer', icon: HandHeart },
    { name: 'Directory', path: '/directory', icon: BookUser },
    { name: 'Become a Coach', path: '/coach-application', icon: Award, highlight: true }
  ];

  // Bottom nav - most used items
  const bottomNavItems = [
    { name: 'Home', path: '/dashboard', icon: Home },
    { name: 'Schedule', path: '/schedule', icon: Calendar },
    { name: 'Comms', path: '/communications', icon: MessageSquare },
    { name: 'Family', path: '/family', icon: Users },
    { name: 'More', path: null, icon: Menu, isMore: true }
  ];

  // Staff-view navigation arrays.
  //
  // Items with a `children` array render as a dropdown (desktop) /
  // accordion (mobile). The parent tab's `path` is what a plain click
  // navigates to when the caller wants a "quick-jump" (also used as the
  // primary active-state anchor). Active-highlighting for a parent tab
  // includes any of its children's paths — so "Accountability" stays
  // highlighted while the admin is on Academics, etc.
  const staffNavigation = isAdmin ? [
    { name: 'Dashboard', path: '/admin/dashboard', icon: Shield },
    { name: 'Teams', path: '/admin/teams', icon: Users },
    { name: 'Applications', path: '/admin/coach-applications', icon: UserPlus },
    {
      name: 'Accountability',
      path: '/admin/accountability',
      icon: Award,
      children: [
        { name: 'Accountability', path: '/admin/accountability' },
        { name: 'Academics', path: '/admin/academics' },
      ],
    },
    ...(isFinanceRole ? [{
      name: 'Finances',
      path: '/admin/finances',
      icon: DollarSign,
      children: [
        { name: 'Finances', path: '/admin/finances' },
        { name: 'Payments', path: '/admin/payments' },
      ],
    }] : []),
    ...(user?.role === 'super_admin' ? [{ name: 'Age Mismatch', path: '/admin/age-mismatch', icon: ShieldAlert }] : []),
    ...(['super_admin', 'league_admin', 'treasurer'].includes(user?.role) ? [{ name: 'SMS Status', path: '/admin/sms-status', icon: MessageSquare }] : []),
    {
      name: 'Moderation',
      path: '/admin/community',
      icon: MessageSquare,
      children: [
        { name: 'Moderation', path: '/admin/community' },
        { name: 'Volunteers', path: '/admin/volunteers' },
        // Announcements management surface — SUPER_ADMIN only for MVP.
        // Other admin roles keep the existing dashboard "Send Announcement"
        // flow; the dedicated management page is deliberately gated.
        ...(user?.role === 'super_admin'
          ? [{ name: 'Announcements', path: '/admin/announcements' }]
          : []),
      ],
    },
    {
      name: 'Users',
      path: '/admin/users',
      icon: Users,
      children: [
        { name: 'Users', path: '/admin/users' },
        { name: 'Staff', path: '/admin/staff' },
      ],
    },
  ] : isCoach ? [
    { name: 'Dashboard', path: '/coach/dashboard', icon: Shield },
    { name: 'My Team', path: '/coach/team', icon: Users },
    { name: 'Schedule', path: '/schedule', icon: Calendar },
    { name: 'Comms', path: '/communications', icon: MessageSquare },
    { name: 'Directory', path: '/directory', icon: BookUser },
  ] : [];

  const activeNavigation = isDualRole && activeView !== 'parent' ? staffNavigation : navigation;

  // Dropdown state for grouped staff nav items (Accountability, Finances,
  // Moderation, Users). Only one dropdown open at a time. Mobile uses
  // the same state as expandable accordion.
  const [openNavGroup, setOpenNavGroup] = useState(null);

  // Close any open dropdown whenever the route changes.
  useEffect(() => {
    setOpenNavGroup(null);
  }, [location.pathname]);

  // Match a nav item against the current route, including any of its
  // grouped children. Keeps the parent tab highlighted while an admin
  // is on a child page (e.g. Accountability stays lit while on
  // /admin/academics).
  const isNavItemActive = (item) => {
    if (location.pathname === item.path) return true;
    if (item.children && item.children.some((c) => c.path === location.pathname)) return true;
    return false;
  };

  // Bottom nav adapts to active view
  const staffBottomNavItems = isAdmin ? [
    { name: 'Admin', path: '/admin/dashboard', icon: Shield },
    { name: 'Teams', path: '/admin/teams', icon: Users },
    { name: 'Schedule', path: '/schedule', icon: Calendar },
    { name: 'Comms', path: '/communications', icon: MessageSquare },
    { name: 'More', path: null, icon: Menu, isMore: true }
  ] : [
    { name: 'Coach', path: '/coach/dashboard', icon: Shield },
    { name: 'Team', path: '/coach/team', icon: Users },
    { name: 'Schedule', path: '/schedule', icon: Calendar },
    { name: 'Comms', path: '/communications', icon: MessageSquare },
    { name: 'More', path: null, icon: Menu, isMore: true }
  ];

  const activeBottomNavItems = isDualRole && activeView !== 'parent' ? staffBottomNavItems : bottomNavItems;

  const handleBottomNavClick = (item) => {
    if (item.isMore) {
      setMobileMenuOpen(true);
    } else {
      navigate(item.path);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Header */}
      <header 
        className={`bg-navy sticky top-0 z-50 transition-shadow duration-200 ${
          scrolled ? 'shadow-lg' : ''
        }`} 
        data-testid="parent-header"
      >
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo & Brand */}
            <Link to="/dashboard" className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                <img 
                  src="/logos/arrowhead.png" 
                  alt="Park City Chiefs" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-white font-bold text-base sm:text-lg leading-tight">Park City Chiefs</h1>
                <p className="text-light-blue/80 text-xs">Youth Football</p>
              </div>
            </Link>

            {/* Role Switcher + Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-4">
              {isDualRole && (
                <div className="flex items-center bg-white/10 rounded-full p-0.5 shrink-0" data-testid="role-switcher">
                  <button
                    onClick={() => handleViewSwitch('parent')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                      activeView === 'parent'
                        ? 'bg-gold text-navy shadow-sm'
                        : 'text-white/60 hover:text-white'
                    }`}
                    data-testid="role-switch-parent"
                  >
                    <Users className="w-3.5 h-3.5" />
                    Parent
                  </button>
                  <button
                    onClick={() => handleViewSwitch('staff')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                      activeView !== 'parent'
                        ? 'bg-gold text-navy shadow-sm'
                        : 'text-white/60 hover:text-white'
                    }`}
                    data-testid="role-switch-staff"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    {staffLabel}
                  </button>
                </div>
              )}

              <nav className="flex items-center space-x-1">
                {activeNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = isNavItemActive(item);
                  const showBadge = item.path === '/communications' && unreadCount > 0;
                  const testIdBase = `nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`;

                  // Grouped dropdown item (Accountability / Finances /
                  // Moderation / Users). Renders a button that toggles
                  // a compact dropdown with the child links.
                  if (item.children) {
                    const isOpen = openNavGroup === item.name;
                    return (
                      <div key={item.name} className="relative">
                        <button
                          type="button"
                          onClick={() => setOpenNavGroup(isOpen ? null : item.name)}
                          className={`relative flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-gold text-navy'
                              : 'text-white/80 hover:text-white hover:bg-white/10'
                          }`}
                          data-testid={`${testIdBase}-group`}
                          aria-haspopup="menu"
                          aria-expanded={isOpen}
                        >
                          <Icon className="w-4 h-4" />
                          <span>{item.name}</span>
                          <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {isOpen && (
                          <div
                            className="absolute left-0 top-full mt-2 w-52 bg-navy border border-white/10 rounded-lg shadow-xl py-1 z-50"
                            role="menu"
                            data-testid={`${testIdBase}-dropdown`}
                          >
                            {item.children.map((child) => {
                              const childActive = location.pathname === child.path;
                              return (
                                <button
                                  key={child.path}
                                  type="button"
                                  onClick={() => {
                                    setOpenNavGroup(null);
                                    navigate(child.path);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                    childActive
                                      ? 'bg-gold text-navy font-semibold'
                                      : 'text-white/85 hover:bg-white/10 hover:text-white'
                                  }`}
                                  data-testid={`${testIdBase}-child-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                                  role="menuitem"
                                >
                                  {child.name}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Standard single nav item.
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`relative flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-gold text-navy'
                          : item.highlight
                          ? 'text-gold hover:text-gold hover:bg-white/10 border border-gold/50'
                          : 'text-white/80 hover:text-white hover:bg-white/10'
                      }`}
                      data-testid={testIdBase}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                      {showBadge && (
                        <span className="absolute -top-1 -right-1 bg-red text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Right Actions */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              {/* User Menu - Desktop */}
              <div className="hidden lg:flex items-center space-x-3 relative">
                <button 
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 hover:bg-white/10 rounded-lg px-3 py-2 transition"
                >
                  <div className="text-right">
                    <p className="text-white text-sm font-medium">{user?.name}</p>
                    <p className="text-light-blue/70 text-xs capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={() => { navigate('/guest'); setUserMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="menu-home-page-link"
                      >
                        <Globe className="w-4 h-4" />
                        Visit Home Page
                      </button>
                      <button
                        onClick={() => { setShowNotificationSettings(true); setUserMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Bell className="w-4 h-4" />
                        Notification Settings
                      </button>
                      <button
                        onClick={() => { navigate('/communications?tab=community&faq=open'); setUserMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                        data-testid="menu-faq-link"
                      >
                        <HelpCircle className="w-4 h-4" />
                        Parent FAQ
                      </button>
                    </div>
                    <div className="border-t border-gray-100 py-1">
                      <button
                        onClick={() => { logout(); setUserMenuOpen(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-white rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Slide-down Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-navy border-t border-white/10 animate-in slide-in-from-top duration-200">
            <div className="px-4 py-4 max-h-[70vh] overflow-y-auto">
              {/* User Info */}
              <div className="flex items-center space-x-3 pb-4 mb-4 border-b border-white/10">
                <div className="w-10 h-10 bg-light-blue/20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-light-blue" />
                </div>
                <div>
                  <p className="text-white font-medium">{user?.name}</p>
                  <p className="text-light-blue/70 text-xs capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>

              {/* Role Switcher - Mobile */}
              {isDualRole && (
                <div className="flex items-center bg-white/5 rounded-xl p-1 mb-4" data-testid="mobile-role-switcher">
                  <button
                    onClick={() => handleViewSwitch('parent')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      activeView === 'parent'
                        ? 'bg-gold text-navy shadow-sm'
                        : 'text-white/60 hover:text-white'
                    }`}
                    data-testid="mobile-role-switch-parent"
                  >
                    <Users className="w-4 h-4" />
                    Parent
                  </button>
                  <button
                    onClick={() => handleViewSwitch('staff')}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      activeView !== 'parent'
                        ? 'bg-gold text-navy shadow-sm'
                        : 'text-white/60 hover:text-white'
                    }`}
                    data-testid="mobile-role-switch-staff"
                  >
                    <Shield className="w-4 h-4" />
                    {staffLabel}
                  </button>
                </div>
              )}

              {/* Navigation */}
              <div className="space-y-1 mb-4">
                {activeNavigation.map((item) => {
                  const Icon = item.icon;
                  const isActive = isNavItemActive(item);
                  const showBadge = item.path === '/communications' && unreadCount > 0;
                  const testIdBase = `mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`;

                  // Grouped accordion row (Accountability / Finances /
                  // Moderation / Users) — expands to reveal child links.
                  if (item.children) {
                    const isOpen = openNavGroup === item.name;
                    return (
                      <div key={item.name}>
                        <button
                          type="button"
                          onClick={() => setOpenNavGroup(isOpen ? null : item.name)}
                          className={`w-full flex items-center justify-between px-3 py-3 rounded-lg transition-colors ${
                            isActive
                              ? 'bg-gold text-navy'
                              : 'text-white hover:bg-white/10'
                          }`}
                          aria-expanded={isOpen}
                          data-testid={`${testIdBase}-group`}
                        >
                          <span className="flex items-center space-x-3">
                            <Icon className="w-5 h-5" />
                            <span className="font-medium">{item.name}</span>
                          </span>
                          <ChevronDown
                            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        {isOpen && (
                          <div className="pl-6 mt-1 space-y-1">
                            {item.children.map((child) => {
                              const childActive = location.pathname === child.path;
                              return (
                                <Link
                                  key={child.path}
                                  to={child.path}
                                  className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                                    childActive
                                      ? 'bg-gold text-navy font-semibold'
                                      : 'text-white/85 hover:bg-white/10 hover:text-white'
                                  }`}
                                  data-testid={`${testIdBase}-child-${child.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  {child.name}
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`relative flex items-center space-x-3 px-3 py-3 rounded-lg transition-colors ${
                        isActive
                          ? 'bg-gold text-navy'
                          : 'text-white hover:bg-white/10'
                      }`}
                      data-testid={testIdBase}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.name}</span>
                      {showBadge && (
                        <span className="ml-auto bg-red text-white text-xs font-bold rounded-full min-w-[20px] h-[20px] flex items-center justify-center px-1">
                          {unreadCount > 99 ? '99+' : unreadCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>

              {/* Visit Home Page */}
              <button
                onClick={() => { navigate('/guest'); setMobileMenuOpen(false); }}
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-white hover:bg-white/10 w-full transition-colors"
                data-testid="mobile-home-page-link"
              >
                <Globe className="w-5 h-5" />
                <span className="font-medium">Visit Home Page</span>
              </button>

              {/* Notification Settings Button */}
              <button
                onClick={() => { setShowNotificationSettings(true); setMobileMenuOpen(false); }}
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-white hover:bg-white/10 w-full transition-colors"
              >
                <Bell className="w-5 h-5" />
                <span className="font-medium">Notification Settings</span>
              </button>

              {/* Parent FAQ Button */}
              <button
                onClick={() => { navigate('/communications?tab=community&faq=open'); setMobileMenuOpen(false); }}
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-white hover:bg-white/10 w-full transition-colors"
                data-testid="mobile-faq-link"
              >
                <HelpCircle className="w-5 h-5" />
                <span className="font-medium">Parent FAQ</span>
              </button>

              {/* Logout Button */}
              <button
                onClick={logout}
                className="flex items-center space-x-3 px-3 py-3 rounded-lg text-white hover:bg-red/20 w-full transition-colors"
                data-testid="mobile-logout-button"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Sign Out</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Role View Accent Bar */}
      {isDualRole && (
        <div
          className={`h-1 transition-colors duration-300 ${
            activeView === 'parent' ? 'bg-gold' : isAdmin ? 'bg-red' : 'bg-light-blue'
          }`}
          data-testid="role-accent-bar"
          aria-hidden="true"
        />
      )}

      {/* Main Content */}
      <main className="pb-20 lg:pb-8 min-h-[calc(100vh-64px)]">
        {children}
      </main>

      {/* Bottom Navigation - Mobile Only */}
      <nav 
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden z-40 safe-area-pb"
        data-testid="bottom-nav"
      >
        <div className="grid grid-cols-5 gap-1 px-2 py-1.5">
          {activeBottomNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = !item.isMore && location.pathname === item.path;
            const showBadge = item.path === '/communications' && unreadCount > 0;
            const showDot = item.isMore && unreadCount > 0;
            return (
              <button
                key={item.name}
                onClick={() => handleBottomNavClick(item)}
                className={`relative flex flex-col items-center justify-center py-1.5 px-1 rounded-lg transition-colors ${
                  isActive 
                    ? 'bg-navy text-white' 
                    : item.isMore && mobileMenuOpen
                    ? 'bg-navy/10 text-navy'
                    : 'text-gray-600 active:bg-gray-100'
                }`}
                data-testid={`bottom-nav-${item.name.toLowerCase()}`}
              >
                <Icon className="w-5 h-5 mb-0.5" />
                <span className="text-[10px] font-medium">{item.name}</span>
                {showBadge && (
                  <span className="absolute top-0 right-1/4 bg-red text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
                {showDot && (
                  <span className="absolute top-0.5 right-1/4 w-2 h-2 bg-red rounded-full animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Notification Settings</h2>
              <button 
                onClick={() => setShowNotificationSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <NotificationSettings />
            </div>
          </div>
        </div>
      )}

      {/* Click outside to close user menu or nav group dropdown */}
      {(userMenuOpen || openNavGroup) && (
        <div 
          className="fixed inset-0 z-40"
          onClick={() => { setUserMenuOpen(false); setOpenNavGroup(null); }}
        />
      )}
    </div>
  );
};

export default ParentLayout;
